import React from 'react'
import Field from '../../../components/field'
import {useWarrantyContext} from '../util/warranty-context'
import {useForm} from 'react-hook-form'
import WarrantyHeader from './warranty-header'
import WarrantyFooter from './warranty-footer'
import WarrantyFormContainer from './warranty-form-container'
import WarrantyStepContainer from './warranty-step-container'
import {Box, Flex, Text} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import PointOfPurchaseStoreLocator from './point-of-purchase-store-locator'
import {POINTOFPURCHASE_OPTIONS} from '../../../constants'

const PointOfPurchase = () => {
    const {
        data: {
            userInputSelectedProductName,
            pointOfPurchase,
            userInputRetailerName,
            userInputStoreUrl,
            userInputRetailerStreet,
            userInputRetailerCity,
            userInputRetailerCountry
        } = {},
        onSubmit
    } = useWarrantyContext()

    const {formatMessage} = useIntl()

    const form = useForm({
        defaultValues: {
            pointOfPurchase: pointOfPurchase ?? 'store',
            userInputRetailerName,
            userInputStoreUrl,
            userInputRetailerStreet,
            userInputRetailerCity,
            userInputRetailerCountry
        },
        shouldUnregister: true
    })

    const selectedPointOfPurchase = form.watch('pointOfPurchase')

    const fields = {
        pointOfPurchase: {
            form: 'pointOfPurchaseForm',
            name: 'pointOfPurchase',
            label: formatMessage({
                defaultMessage: 'Please choose a point of purchase',
                id: 'warranty.pop.choose_pop'
            }),
            type: 'radio',
            inputProps: {
                autoComplete: 'off',
                isRequired: true
            },
            options: Object.entries(POINTOFPURCHASE_OPTIONS)
                .filter(([, value]) => value.display)
                .map(([key, value]) => ({
                    value: key,
                    label: formatMessage(value.label)
                })),
            control: form.control,
            error: form.formState.errors.pointOfPurchase
        },
        userInputStoreUrl: {
            name: 'userInputStoreUrl',
            label: formatMessage({
                defaultMessage: 'Where did you buy it from?',
                id: 'warranty.pop.where_buy'
            }),
            type: 'text',
            inputProps: {
                autoComplete: 'off',
                form: 'pointOfPurchaseForm',
                isRequired: true
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'global.error.field_is_required'
                })
            },
            control: form.control,
            error: form.formState.errors.userInputStoreUrl,
            helpText: formatMessage({
                defaultMessage: 'Example: www.example.com',
                id: 'warranty.pop.example_url'
            })
        },
        userInputRetailerName: {
            name: 'userInputRetailerName',
            label: formatMessage({
                defaultMessage: 'Retailer name',
                id: 'warranty.pop.retailer_name'
            }),
            type: 'text',
            inputProps: {
                autoComplete: 'off',
                form: 'pointOfPurchaseForm',
                isRequired: true
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'global.error.field_is_required'
                })
            },
            control: form.control,
            error: form.formState.errors.userInputRetailerName
        },
        userInputRetailerStreet: {
            name: 'userInputRetailerStreet',
            label: formatMessage({
                defaultMessage: 'Street',
                id: 'warranty.pop.retailer_street'
            }),
            type: 'text',
            inputProps: {
                autoComplete: 'off',
                form: 'pointOfPurchaseForm',
                isRequired: true
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'global.error.field_is_required'
                })
            },
            control: form.control,
            error: form.formState.errors.userInputRetailerStreet
        },
        userInputRetailerCity: {
            name: 'userInputRetailerCity',
            label: formatMessage({
                defaultMessage: 'Retailer city',
                id: 'warranty.pop.retailer_city'
            }),
            type: 'text',
            inputProps: {
                autoComplete: 'off',
                form: 'pointOfPurchaseForm',
                isRequired: true
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'global.error.field_is_required'
                })
            },
            control: form.control,
            error: form.formState.errors.userInputRetailerCity
        },
        userInputRetailerCountry: {
            name: 'userInputRetailerCountry',
            label: formatMessage({
                defaultMessage: 'Retailer country',
                id: 'warranty.pop.retailer_country'
            }),
            type: 'text',
            inputProps: {
                autoComplete: 'off',
                form: 'pointOfPurchaseForm',
                isRequired: true
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'This field is required.',
                    id: 'global.error.field_is_required'
                })
            },
            control: form.control,
            error: form.formState.errors.userInputRetailerCountry
        }
    }

    return (
        <WarrantyStepContainer>
            <WarrantyHeader>
                <FormattedMessage
                    defaultMessage="Where did you buy your product"
                    id="warranty_portal.pop.header"
                />
            </WarrantyHeader>
            <Text>{userInputSelectedProductName}</Text>
            <WarrantyFormContainer columns={1}>
                <Box
                    sx={{
                        ul: {
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    <Field {...fields.pointOfPurchase} />
                </Box>
                {selectedPointOfPurchase === 'store' && (
                    <>
                        <PointOfPurchaseStoreLocator form={form} fields={fields} />
                    </>
                )}
                {selectedPointOfPurchase === 'online' && (
                    <>
                        <Field {...fields.userInputStoreUrl} />
                    </>
                )}
            </WarrantyFormContainer>
            <Flex
                as="form"
                noValidate
                onSubmit={form.handleSubmit(onSubmit)}
                id="pointOfPurchaseForm"
                flexDirection="column"
                gap={6}
            >
                <WarrantyFooter />
            </Flex>
        </WarrantyStepContainer>
    )
}

export default PointOfPurchase
